import { GetServerSideProps } from 'next';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import Layout from '@/components/layout/LayoutWrapper';
import PreSignupFlowContainer from '@/components/PreSignupFlow';
import { PreSignupFlowContextProvider } from '@/components/PreSignupFlow/PreSignupProvider';
import StripeElementsProvider from '@/components/purchase/StripeElementsProvider';
import SimplifiedSignup from '@/components/SimplifiedSignup';
import ControlFlow from '@/components/SimplifiedSignup/configs/Control/Flow';
import { SimplifiedSignupContextProvider } from '@/components/SimplifiedSignup/Context';
import type { FlowScreenNames } from '@/components/SimplifiedSignup/Context/types';
import { shapeConfigForParams } from '@/components/SimplifiedSignup/Context/utils';
import { InitRequest, useUserState } from '@/hooks/store';
import messages from '@/messages/messages';
import type { InitialServerState } from '@/store';
import { calmLogger } from '@/utils/calmLogger';
import { setCookie } from '@/utils/cookies';
import {
	AmplitudeExperimentNames,
	isEnrolledVariant,
	setupAmplitudeExperimentUser,
} from '@/utils/experiments/amplitudeExperiment';
import { resolveCheckoutSessionsEnabledFromVariants } from '@/utils/experiments/resolveCheckoutSessionsEnabled';
import getServerSideFeatureFlags from '@/utils/getServerSideFeatureFlags';
import { getInitialServerState } from '@/utils/pageGeneration/getServerSideProps';

// FlowMap focus values that the post-upsell goals experiment can route paid users into.
// Narrower than `Object.keys(FlowMap)`.
const QUESTIONNAIRE_FOCUS_KEYS = new Set<string>([
	'goals',
	'stress',
	'sleep',
	'stress_questionnaireB',
	'sleep_questionnaireB',
]);

type Props = {
	initialState: InitialServerState | null;
	isPostUpsellGoalsTreatment: boolean;
	isCheckoutSessionsEnabled: boolean;
};

const SignupFlow = ({ isPostUpsellGoalsTreatment, isCheckoutSessionsEnabled }: Props) => {
	const { formatMessage } = useIntl();
	const searchParams = useSearchParams();
	const user = useUserState();

	useEffect(() => {
		setCookie('ftue-show-discount-cta', 'true', { expires: 1 });
		setCookie('ftue-show-discount-modal', 'true', { expires: 1 });
	}, []);

	if (isPostUpsellGoalsTreatment) {
		const isPaid = !!user?.subscription?.valid;
		const step = searchParams?.get('step') ?? null;
		// Refresh on a questionnaire URL lands at the goals screen (which mounts the embedded
		// PreSignupFlow at the right step). Refresh on `?step=handoff` lands at handoff.
		const activeScreenKey: FlowScreenNames = !isPaid ? 'account' : step === 'handoff' ? 'handoff' : 'goals';
		const flowConfig = {
			...ControlFlow,
			activeScreenKey,
			activeScreenConfig: ControlFlow.screens[activeScreenKey],
		};
		return (
			<Layout title={`Calm - ${formatMessage(messages.signupPageTitle)}`} pageName="signup">
				<StripeElementsProvider isStripeNext>
					<SimplifiedSignupContextProvider
						flowConfig={flowConfig}
						isCheckoutSessionsEnabled={isCheckoutSessionsEnabled}
					>
						<SimplifiedSignup />
					</SimplifiedSignupContextProvider>
				</StripeElementsProvider>
			</Layout>
		);
	}

	return (
		<Layout title={`Calm - ${formatMessage(messages.signupPageTitle)}`} pageName="signup">
			<StripeElementsProvider isStripeNext>
				<PreSignupFlowContextProvider isCheckoutSessionsEnabled={isCheckoutSessionsEnabled}>
					<PreSignupFlowContainer />
				</PreSignupFlowContextProvider>
			</StripeElementsProvider>
		</Layout>
	);
};

export const getServerSideProps: GetServerSideProps<Props> = async context => {
	const initialState = await getInitialServerState(
		context,
		[InitRequest.DEVICE, InitRequest.USER, InitRequest.PRICES],
		shapeConfigForParams(ControlFlow),
		undefined,
		true,
	);

	const isPaid = !!initialState?.user?.subscription?.valid;
	const focus = typeof context.query.focus === 'string' ? context.query.focus : undefined;
	const step = typeof context.query.step === 'string' ? context.query.step : undefined;

	const isOnQuestionnaireUrl = !!focus && QUESTIONNAIRE_FOCUS_KEYS.has(focus);
	const isOnHandoff = step === 'handoff';

	// Skip the Amplitude fetch when the variant value will never be read: paid users
	// heading to /app (no focus/step) and treatment users on non-questionnaire URLs.
	const needsExperimentLookup = isPaid ? isOnQuestionnaireUrl || isOnHandoff : true;

	// Non-prod test/QA override — when `?forceCheckoutSessions=treatment|control` is present
	// (and ONLY outside production, so it can never force the form in prod), it wins over the Amplitude
	// resolution below. Lets the E2E specs pin the CS-vs-legacy payment form deterministically,
	// independent of the `www-checkout-sessions` rollout %. `null` = no override (use Amplitude).
	// Fail-CLOSED: require `NEXT_PUBLIC_CALM_ENV` to be a non-empty, non-`production` value, so an unset
	// or misconfigured env can never open this payment-form override in prod (defense-in-depth).
	const calmEnv = process.env.NEXT_PUBLIC_CALM_ENV;
	const { forceCheckoutSessions } = context.query;
	const forcedCheckoutSessionsEnabled =
		!!calmEnv &&
		calmEnv !== 'production' &&
		(forceCheckoutSessions === 'treatment' || forceCheckoutSessions === 'control')
			? forceCheckoutSessions === 'treatment'
			: null;

	let isPostUpsellGoalsTreatment = false;
	// Resolved server-side so the payment screen knows the variant before it mounts
	// (no flicker; the checkout session can be created eagerly). The
	// `www-checkout-sessions` experiment is enrolled at 100% treatment; whether this
	// resolves to `true` depends on the eligibility conjunction and allowlist checked
	// further down the SimplifiedSignup surface, not on the experiment itself.
	let isCheckoutSessionsEnabled = false;
	if (needsExperimentLookup && initialState?.device) {
		const experimentUser = setupAmplitudeExperimentUser(initialState.user ?? null, initialState.device);
		const variants = await getServerSideFeatureFlags(experimentUser);
		isPostUpsellGoalsTreatment = isEnrolledVariant(
			variants[AmplitudeExperimentNames.GOAL_QUESTIONNAIRE_POST_UPSELL],
		);
		// Shares its resolution logic (not the fetch itself, already done above
		// alongside the post-upsell-goals lookup) with every other recurring-subscription
		// SimplifiedSignup surface via `resolveCheckoutSessionsEnabled`. The non-prod override
		// above wins when set (so the diagnostic log below reflects what's actually served).
		isCheckoutSessionsEnabled =
			forcedCheckoutSessionsEnabled ?? resolveCheckoutSessionsEnabledFromVariants(variants);

		// Diagnostic: the SSR variant is evaluated with the identity
		// available at request time (often pre-auth — no user_id until the Account
		// step). The client experiment client re-evaluates with the live identity, so
		// the two can diverge. Log what the server saw so we can compare against the
		// client vardata. The form gate (PaymentElementsFormRefresh) latches whichever
		// value is available at first visible paint — the SSR value in the common in-flow
		// case, since the client experiment is still loading then.
		// Gated to non-prod OR treatment-on so it stays useful for dev-N debugging without
		// adding a high-volume prod info log for the majority of requests that resolve to
		// `false` (ineligible flow/plan, not the experiment being off).
		if (isCheckoutSessionsEnabled || process.env.NEXT_PUBLIC_CALM_ENV !== 'production') {
			calmLogger.info('signup-flow: www-checkout-sessions resolved server-side', {
				variant: variants[AmplitudeExperimentNames.CHECKOUT_SESSIONS]?.value ?? null,
				isCheckoutSessionsEnabled,
				deviceId: experimentUser.device_id ?? null,
				userId: experimentUser.user_id ?? null,
			});
		}
	} else if (forcedCheckoutSessionsEnabled !== null) {
		// No experiment lookup ran (e.g. no device) but still honor the non-prod override.
		isCheckoutSessionsEnabled = forcedCheckoutSessionsEnabled;
	}

	// Unauthenticated treatment users landing on a post-payment URL haven't paid yet —
	// strip the URL state and let the page render SimplifiedSignup at the account screen.
	// (Control users on these URLs are the legitimate pre-upsell www-new-ftue-questionnaire
	// flow; their behavior is unchanged.)
	if (!isPaid && isPostUpsellGoalsTreatment && (isOnQuestionnaireUrl || isOnHandoff)) {
		return {
			redirect: {
				destination: '/signup-flow',
				permanent: false,
			},
		};
	}

	if (isPaid) {
		const stayOnSignupFlow = isPostUpsellGoalsTreatment && (isOnQuestionnaireUrl || isOnHandoff);
		if (!stayOnSignupFlow) {
			return {
				redirect: {
					destination: '/app',
					permanent: false,
				},
			};
		}
	}

	return {
		props: {
			initialState,
			isPostUpsellGoalsTreatment,
			isCheckoutSessionsEnabled,
		},
	};
};

export default SignupFlow;
